import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { HeartHandshake, Copy, QrCode, Landmark } from 'lucide-react';
import { toast } from 'sonner';
import PageHeader from '@/components/ui/PageHeader';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

export default function Doacoes() {
  const { data: churchInfo, isLoading } = useQuery({
    queryKey: ['churchInfo'],
    queryFn: async () => {
      const info = await base44.entities.ChurchInfo.list();
      return info[0] || null;
    },
  });

  const copyToClipboard = async (value, label) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(String(value));
      toast.success(`${label} copiado.`);
    } catch (_error) {
      toast.error('Não foi possível copiar.');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <LoadingSpinner />
      </div>
    );
  }

  const donationTitle = churchInfo?.donation_title || 'Dízimos e Ofertas';
  const donationText =
    churchInfo?.donation_text ||
    'Sua contribuição ajuda a manter os projetos e atividades da igreja.';

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div className="bg-gradient-to-br from-red-600 to-rose-700 text-white pt-8 pb-6 px-4">
        <div className="max-w-4xl mx-auto">
          <PageHeader title="Dízimos e Ofertas" subtitle="Contribua com esta obra" showBack />
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-200">
          {churchInfo?.donation_image_url && (
            <img
              src={churchInfo.donation_image_url}
              alt="Dízimos e Ofertas"
              className="w-full h-56 md:h-72 object-cover"
            />
          )}

          <div className="p-5 md:p-7">
            <div className="flex items-center gap-2 mb-3">
              <HeartHandshake className="w-6 h-6 text-red-600" />
              <h1 className="text-2xl font-bold text-zinc-900">{donationTitle}</h1>
            </div>
            <p className="text-zinc-700 leading-relaxed">{donationText}</p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4 mt-5">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
            <div className="flex items-center gap-2 mb-3">
              <QrCode className="w-5 h-5 text-emerald-700" />
              <h2 className="text-lg font-semibold text-zinc-900">Pix</h2>
            </div>

            {churchInfo?.pix_qr_image_url ? (
              <img
                src={churchInfo.pix_qr_image_url}
                alt="QR Code Pix"
                className="w-48 h-48 object-contain border rounded-xl mx-auto mb-4"
              />
            ) : (
              <p className="text-sm text-gray-500 mb-4">QR Code ainda não configurado.</p>
            )}

            {churchInfo?.pix_key && (
              <div className="space-y-2">
                <Badge variant="secondary" className="bg-emerald-50 text-emerald-700">
                  Chave Pix
                </Badge>
                <p className="text-sm break-all">{churchInfo.pix_key}</p>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => copyToClipboard(churchInfo.pix_key, 'Chave Pix')}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copiar chave Pix
                </Button>
              </div>
            )}

            {churchInfo?.pix_copy_link && (
              <div className="space-y-2 mt-3">
                <Badge variant="secondary" className="bg-emerald-50 text-emerald-700">
                  Pix Copia e Cola
                </Badge>
                <p className="text-sm break-all">{churchInfo.pix_copy_link}</p>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => copyToClipboard(churchInfo.pix_copy_link, 'Pix copia e cola')}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copiar Pix Copia e Cola
                </Button>
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
            <div className="flex items-center gap-2 mb-3">
              <Landmark className="w-5 h-5 text-indigo-700" />
              <h2 className="text-lg font-semibold text-zinc-900">Transferência Bancária</h2>
            </div>

            <div className="space-y-2 text-sm">
              {churchInfo?.bank_name && <p><strong>Banco:</strong> {churchInfo.bank_name}</p>}
              {churchInfo?.bank_agency && <p><strong>Agência:</strong> {churchInfo.bank_agency}</p>}
              {churchInfo?.bank_account && <p><strong>Conta:</strong> {churchInfo.bank_account}</p>}
              {churchInfo?.bank_account_type && <p><strong>Tipo:</strong> {churchInfo.bank_account_type}</p>}
              {churchInfo?.bank_beneficiary && <p><strong>Favorecido:</strong> {churchInfo.bank_beneficiary}</p>}
              {churchInfo?.bank_document && <p><strong>CPF/CNPJ:</strong> {churchInfo.bank_document}</p>}
            </div>

            {churchInfo?.bank_account && (
              <Button
                variant="outline"
                className="w-full mt-4"
                onClick={() => copyToClipboard(churchInfo.bank_account, 'Conta')}
              >
                <Copy className="w-4 h-4 mr-2" />
                Copiar conta
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
